package co.maytes.standupbot.dashboard.features.standupruns.tabs

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.feedback.EmptyState
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideMessagesSquare
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standuprun.UserSectionDto
import co.maytes.standupbot.shared.dto.standuprun.UserSectionStatusDto
import co.maytes.standupbot.shared.template.SectionKey
import co.maytes.standupbot.shared.template.SectionsConfigDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

@Composable
internal fun SectionsTab(
    sections: List<UserSectionDto>,
    layout: SectionsConfigDto = SectionsConfigDto.DEFAULT,
) {
    if (sections.isEmpty()) {
        EmptyState(
            title = "No user sections",
            subtitle = "This run produced no per-member sections — likely because no one logged activity in the lookback window.",
            icon = { LucideMessagesSquare(size = 26) },
        )
        return
    }
    Column(modifier = Modifier.fillMaxWidth()) {
        sections.sortedBy { it.orderIndex }.forEach { section ->
            UserSectionCard(section, layout)
        }
    }
}

@Composable
private fun UserSectionCard(
    section: UserSectionDto,
    layout: SectionsConfigDto,
) {
    Card(modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.md)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            SpanText(
                text = section.displayName,
                modifier = Modifier
                    .fontSize(Typography.body.size)
                    .fontWeight(600)
                    .color(Palette.textPrimary),
            )
            UserSectionStatusChip(section.status)
        }
        if (section.status == UserSectionStatusDto.ON_LEAVE) {
            SectionField(label = "Status", text = "On leave. Left out of this standup.")
        } else {
            layout.includedSlots().forEach { slot ->
                SectionField(label = slot.label, text = section.textFor(slot.key))
            }
        }
        section.userAddedContext?.takeIf { it.isNotBlank() }?.let {
            SectionField(label = "User-added context", text = it, accent = true)
        }
    }
}

private fun UserSectionDto.textFor(key: SectionKey): String =
    when (key) {
        SectionKey.YESTERDAY -> yesterday
        SectionKey.TODAY -> today
        SectionKey.BLOCKERS -> blockers
    }

@Composable
private fun SectionField(label: String, text: String, accent: Boolean = false) {
    Column(modifier = Modifier.fillMaxWidth().margin(top = Spacing.sm)) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(if (accent) Palette.accent else Palette.textSecondary),
        )
        SpanText(
            text = text.ifBlank { "—" },
            modifier = Modifier
                .margin(top = Spacing.xxs)
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textPrimary)
                .styleModifier { property("white-space", "pre-wrap") },
        )
    }
}

@Composable
private fun UserSectionStatusChip(status: UserSectionStatusDto) {
    val (label, bg, fg) = when (status) {
        UserSectionStatusDto.GENERATED -> Triple("generated", Palette.surfaceSubtle, Palette.textSecondary)
        UserSectionStatusDto.EDITED -> Triple("edited", Palette.accentSubtle, Palette.accent)
        UserSectionStatusDto.EMPTY -> Triple("empty", Palette.surfaceSubtle, Palette.textSecondary)
        UserSectionStatusDto.OPTED_OUT -> Triple("opted-out", Palette.surfaceSubtle, Palette.warning)
        UserSectionStatusDto.ON_LEAVE -> Triple("on leave", Palette.surfaceSubtle, Palette.textTertiary)
        UserSectionStatusDto.SKIPPED_TODAY -> Triple("skipped", Palette.surfaceSubtle, Palette.textTertiary)
    }
    Box(
        modifier = Modifier
            .backgroundColor(bg)
            .color(fg)
            .padding(topBottom = Spacing.xxs, leftRight = Spacing.xs)
            .borderRadius(Radius.full),
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(500),
        )
    }
}
